import { supabase } from "@/integrations/supabase/client";
import { Order, OrderItem, OrderStatus } from "@/types/orders";
import { trackInventoryChange } from "@/services/inventory/inventoryTracker";
import { handleOrderNotification } from "./orderNotification.service";

/**
 * Get active orders for a specific table
 * @param tableId The table ID to fetch orders for
 * @returns Array of active orders
 */
export const getActiveOrdersForTable = async (
  tableId: string
): Promise<Order[]> => {
  try {
    const { data: ordersData, error: ordersError } = await supabase
      .from("orders")
      .select("*")
      .eq("table_id", tableId)
      .in("status", ["pending", "ready", "preparing", "completed"])
      .order("created_at", { ascending: false });

    if (ordersError) {
      console.error("Error fetching orders:", ordersError);
      return [];
    }

    if (!ordersData || ordersData.length === 0) {
      return [];
    }

    // Fetch order items for each order
    const orders = await Promise.all(
      ordersData.map(async (order) => {
        const { data: itemsData, error: itemsError } = await supabase
          .from("order_items")
          .select("*")
          .eq("order_id", order.id);

        if (itemsError) {
          console.error("Error fetching order items:", itemsError);
          return {
            id: order.id,
            store_id: order.store_id,
            table_id: order.table_id,
            tableNumber: order.table_number,
            tableName: order.table_name,
            status: order.status as OrderStatus,
            totalAmount: order.total_amount,
            items: [],
            specialInstructions: order.special_instructions,
            createdAt: order.created_at,
            updatedAt: order.updated_at,
            is_payed: order.is_payed,
          };
        }

        // Fetch images for each menu item
        const menuItemIds =
          itemsData
            ?.filter((item) => item.menu_item_id)
            .map((item) => item.menu_item_id) || [];

        let imageMap = new Map();
        if (menuItemIds.length > 0) {
          const { data: menuItems } = await supabase
            .from("menu_items")
            .select("id, image_url")
            .in("id", menuItemIds);

          if (menuItems && menuItems.length > 0) {
            imageMap = new Map(
              menuItems.map((item) => [item.id, item.image_url])
            );
          }
        }

        // Format order items
        const formattedItems: OrderItem[] =
          itemsData?.map((item) => ({
            id: item.id,
            order_id: item.order_id,
            menu_item_id: item.menu_item_id,
            menu_item_name: item.menu_item_name,
            price: item.price,
            quantity: item.quantity,
            notes: item.notes,
            created_at: item.created_at,
            updated_at: item.updated_at,
            imageUrl: item.menu_item_id
              ? imageMap.get(item.menu_item_id)
              : undefined,
          })) || [];

        // Format and return the complete order
        return {
          id: order.id,
          store_id: order.store_id,
          table_id: order.table_id,
          tableNumber: order.table_number,
          tableName: order.table_name,
          status: order.status as OrderStatus,
          totalAmount: order.total_amount,
          items: formattedItems,
          specialInstructions: order.special_instructions,
          createdAt: order.created_at,
          updatedAt: order.updated_at,
          is_payed: order.is_payed,
        };
      })
    );
    return orders;
  } catch (error) {
    console.error("Error in getActiveOrdersForTable:", error);
    return [];
  }
};

/**
 * Create a new order
 * @param order The order to create
 * @returns The created order or null on error
 */
export const createOrder = async (
  order: Omit<Order, "id" | "createdAt" | "updatedAt">
): Promise<Order | null> => {
  try {
    // Validate inventory availability before creating order
    for (const item of order.items) {
      const { data: inventoryRelations, error: relError } = await supabase
        .from("menu_item_inventory")
        .select(
          `
          inventory_item_id,
          quantity_required,
          inventory_items!inner (
            id,
            current_quantity,
            inventory_type,
            name
          )
        `
        )
        .eq("menu_item_id", item.menu_item_id);

      if (relError) {
        console.error("Error checking inventory:", relError);
        throw new Error(
          `Failed to validate inventory for ${item.menu_item_name}`
        );
      }

      // Check each inventory relationship
      for (const rel of inventoryRelations || []) {
        const requiredQuantity = rel.quantity_required * item.quantity;
        if (rel.inventory_items.current_quantity < requiredQuantity) {
          throw new Error(
            `We're sorry, but we don't have enough ${rel.inventory_items.name} to prepare ${item.menu_item_name}. Please adjust your order or try something else.`
          );
        }
      }
    }

    // Start a Supabase transaction
    const { data: orderData, error: orderError } = await supabase
      .from("orders")
      .insert({
        store_id: order.store_id,
        table_id: order.table_id,
        table_number: order.tableNumber,
        table_name: order.tableName,
        status: order.status,
        total_amount: order.totalAmount,
        special_instructions: order.specialInstructions,
        payment_method: order.payment_method,
      })
      .select()
      .single();

    if (orderError || !orderData) {
      console.error("Error creating order:", orderError);
      return null;
    }

    // Insert order items and deduct advanced inventory
    if (order.items && order.items.length > 0) {
      const orderItems = order.items.map((item) => ({
        order_id: orderData.id,
        menu_item_id: item.menu_item_id,
        menu_item_name: item.menu_item_name,
        price: item.price,
        quantity: item.quantity,
        notes: item.notes,
      }));

      // Insert order items
      const { error: itemsError } = await supabase
        .from("order_items")
        .insert(orderItems);

      if (itemsError) {
        console.error("Error creating order items:", itemsError);
        // Attempt to rollback the order
        await supabase.from("orders").delete().eq("id", orderData.id);
        return null;
      }

      // Deduct advanced inventory immediately
      for (const item of order.items) {
        const { data: relations, error: relError } = await supabase
          .from("menu_item_inventory")
          .select(
            `
            inventory_item_id,
            quantity_required,
            inventory_items!inner (
              id,
              current_quantity,
              inventory_type,
              name,
              store_id,
              unit_of_measure,
              minimum_quantity
            )
          `
          )
          .eq("menu_item_id", item.menu_item_id)
          .eq("inventory_items.inventory_type", "advanced");

        if (relError) {
          console.error("Error fetching inventory relations:", relError);
          continue;
        }

        // Update each advanced inventory item
        for (const rel of relations || []) {
          const deductAmount = rel.quantity_required * item.quantity;
          const newQuantity = Math.max(
            0,
            rel.inventory_items.current_quantity - deductAmount
          );

          const { error: updateError } = await supabase
            .from("inventory_items")
            .update({
              current_quantity: newQuantity,
              updated_at: new Date().toISOString(),
            })
            .eq("id", rel.inventory_item_id)
            .eq("inventory_type", "advanced");

          if (!updateError) {
            // Track inventory change
            try {
              await trackInventoryChange({
                inventoryItemId: rel.inventory_item_id,
                storeId: rel.inventory_items.store_id,
                previousQuantity: rel.inventory_items.current_quantity,
                currentQuantity: newQuantity,
                minimumQuantity: rel.inventory_items.minimum_quantity,
                itemName: rel.inventory_items.name,
                unitOfMeasure: rel.inventory_items.unit_of_measure,
              });
            } catch (trackingError) {
              console.warn(
                "Inventory tracking failed (non-critical):",
                trackingError
              );
            }
          }
        }
      }
    }

    // Return the created order
    return {
      id: orderData.id,
      store_id: orderData.store_id,
      table_id: orderData.table_id,
      tableNumber: orderData.table_number,
      tableName: orderData.table_name,
      status: orderData.status as OrderStatus,
      totalAmount: orderData.total_amount,
      items: order.items || [],
      specialInstructions: orderData.special_instructions,
      createdAt: orderData.created_at,
      updatedAt: orderData.updated_at,
      is_payed: orderData.is_payed,
    };
  } catch (error) {
    console.error("Error in createOrder:", error);
    throw error;
  }
};

/**
 * Submit an order - processes both the order and its items
 * @param orderData Basic order data
 * @param orderItems Order items data
 * @returns The created order or null on error
 */
export const submitOrder = async (
  orderData: {
    id: string;
    storeId: string;
    tableId: string;
    tableNumber: number;
    tableName: string;
    status: OrderStatus;
    totalAmount: number;
    specialInstructions?: string;
    payment_method: "Cash" | "Digital";
    is_payed?: boolean;
    tip_amount?: number;
    tip_percentage?: number;
  },
  orderItems: {
    id: string;
    order_id: string;
    menuItemId: string;
    menuItemName: string;
    quantity: number;
    price: number;
    image_url: string;
  }[]
): Promise<Order | null> => {
  try {
    // Cast status to string to allow for Supabase compatibility
    const statusValue = orderData.status;

    // Insert the order
    const { data, error: orderError } = await supabase
      .from("orders")
      .insert({
        id: orderData.id,
        store_id: orderData.storeId,
        table_id: orderData.tableId,
        table_number: orderData.tableNumber,
        table_name: orderData.tableName,
        status: statusValue,
        total_amount: orderData.totalAmount,
        special_instructions: orderData.specialInstructions,
        payment_method: orderData.payment_method,
        is_payed: orderData.is_payed || false, // Default to false if not provided
        tip_amount: orderData.tip_amount || 0,
        tip_percentage: orderData.tip_percentage || 0,
      })
      .select()
      .single();

    if (orderError) {
      console.error("Error submitting order:", orderError);
      return null;
    }

    const order = data;

    // Insert order items
    if (orderItems.length > 0) {
      const formattedItems = orderItems.map((item) => ({
        id: item.id,
        order_id: item.order_id,
        menu_item_id: item.menuItemId,
        menu_item_name: item.menuItemName,
        quantity: item.quantity,
        price: item.price,
      }));

      // Try using the function first (most reliable)
      try {
        const { error: fnError } = await supabase.rpc(
          "insert_public_order_items",
          {
            items: formattedItems,
          }
        );

        if (fnError) {
          console.error("Error using RPC to insert items:", fnError);
          throw fnError; // Fall back to direct insert
          }
      } catch (rpcError) {
        console.warn("Falling back to direct insert:", rpcError);

        // Fallback to direct insert if RPC fails
        const { error: directError } = await supabase
          .from("order_items")
          .insert(formattedItems);

        if (directError) {
          console.error(
            "Error with direct insert of order items:",
            directError
          );
        }
      }
    }

    // Return formatted order
    if (order) {
      const formattedOrder = {
        id: order.id,
        store_id: order.store_id,
        table_id: order.table_id,
        tableNumber: order.table_number,
        tableName: order.table_name,
        status: order.status as OrderStatus,
        totalAmount: order.total_amount,
        items: orderItems.map((item) => ({
          id: item.id,
          order_id: item.order_id,
          menu_item_id: item.menuItemId,
          menu_item_name: item.menuItemName,
          price: item.price,
          quantity: item.quantity,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })),
        specialInstructions: order.special_instructions,
        createdAt: order.created_at,
        updatedAt: order.updated_at,
        is_payed: order.is_payed,
        payment_method: order.payment_method,
        tip_amount: order.tip_amount,
        tip_percentage: order.tip_percentage,
      };

      // Send notification for new order
      try {
        await handleOrderNotification(formattedOrder, 'placed');
      } catch (notificationError) {
        console.error('❌ Error sending order notification:', notificationError);
        // Don't fail the order creation if notification fails
      }

      return formattedOrder;
    }

    return null;
  } catch (error) {
    console.error("Error in submitOrder:", error);
    return null;
  }
};

// Set up a global channel for all subscriptions

/**
 * Initialize global subscription for all orders
 * @param storeId Optional store ID to filter by
 */
export const initGlobalOrderSubscription = (storeId?: string) => {
  // This function sets up global listeners but actual subscriptions are handled by subscribeToStoreOrders
};

/**
 * Subscribe to global order changes across all stores
 */
export const subscribeToGlobalOrders = () => {
  // In a real implementation, this would set up listeners for all stores
  return () => {
    console.log("Global order subscription cleaned up");
  };
};

/**
 * Subscribe to order changes for a specific store
 * @param storeId The store ID to subscribe to
 * @param callback Function to call when an order changes
 * @returns A function to unsubscribe
 */
export const subscribeToStoreOrders = (
  storeId: string,
  callback: (order: Order) => void
) => {
  console.log("Setting up order subscription for store:", storeId);

  if (!storeId) {
    console.warn("No store ID provided for order subscription");
    return () => {};
  }

  const channel = supabase
    .channel(`store_orders_${storeId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "orders",
        filter: `store_id=eq.${storeId}`,
      },
      (payload) => {
        if (payload.new && typeof callback === "function") {
          // Type guard to ensure payload.new exists and has expected properties
          const newData = payload.new as Record<string, unknown>;

          // Format the order data to match our Order type
          const order = {
            id: newData.id || "",
            store_id: newData.store_id || "",
            table_id: newData.table_id || "",
            tableNumber: newData.table_number || 0,
            tableName: newData.table_name || "",
            status: newData.status || "pending",
            totalAmount: newData.total_amount || 0,
            specialInstructions: newData.special_instructions || "",
            createdAt: newData.created_at || new Date().toISOString(),
            updatedAt: newData.updated_at || new Date().toISOString(),
            items: [], // Items will be fetched separately if needed
          };

          callback(order as Order);
        }
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Update order status with notification
 * @param orderId The order ID to update
 * @param newStatus The new status
 * @param previousStatus The previous status (for notification)
 * @returns Promise with success status
 */
export const updateOrderStatus = async (
  orderId: string,
  newStatus: OrderStatus,
  previousStatus?: OrderStatus
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Update the order status
    const { data: updatedOrder, error } = await supabase
      .from("orders")
      .update({ status: newStatus })
      .eq("id", orderId)
      .select()
      .single();

    if (error) {
      console.error("Error updating order status:", error);
      return { success: false, error: error.message };
    }

    // Send notification for status update
    if (updatedOrder && previousStatus) {
      try {
        
        // Get order items for complete order data
        const { data: orderItems } = await supabase
          .from("order_items")
          .select("*")
          .eq("order_id", orderId);

        const completeOrder = {
          id: updatedOrder.id,
          store_id: updatedOrder.store_id,
          table_id: updatedOrder.table_id,
          tableNumber: updatedOrder.table_number,
          tableName: updatedOrder.table_name,
          status: updatedOrder.status as OrderStatus,
          totalAmount: updatedOrder.total_amount,
          items: orderItems?.map(item => ({
            id: item.id,
            order_id: item.order_id,
            menu_item_id: item.menu_item_id,
            menu_item_name: item.menu_item_name,
            price: item.price,
            quantity: item.quantity,
            created_at: item.created_at,
            updated_at: item.updated_at,
          })) || [],
          specialInstructions: updatedOrder.special_instructions,
          createdAt: updatedOrder.created_at,
          updatedAt: updatedOrder.updated_at,
          is_payed: updatedOrder.is_payed,
          payment_method: updatedOrder.payment_method,
          tip_amount: updatedOrder.tip_amount,
          tip_percentage: updatedOrder.tip_percentage,
        };

        await handleOrderNotification(completeOrder, 'status_updated', previousStatus);
        console.log('✅ Order status update notification sent successfully');
      } catch (notificationError) {
        console.error('❌ Error sending status update notification:', notificationError);
        // Don't fail the status update if notification fails
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error in updateOrderStatus:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error updating order status"
    };
  }
};

/**
 * Cancel an order with notification
 * @param orderId The order ID to cancel
 * @returns Promise with success status
 */
export const cancelOrder = async (orderId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get current order status for notification
    const { data: currentOrder } = await supabase
      .from("orders")
      .select("status")
      .eq("id", orderId)
      .single();

    const previousStatus = currentOrder?.status as OrderStatus;

    // Update the order status to cancelled
    const { error } = await supabase
      .from("orders")
      .update({ status: "cancelled" })
      .eq("id", orderId);

    if (error) {
      console.error("Error cancelling order:", error);
      return { success: false, error: error.message };
    }

    // Send notification for cancellation
    if (previousStatus) {
      try {
        const { handleOrderNotification } = await import('./orderNotification.service');
        
        // Get complete order data
        const { data: order } = await supabase
          .from("orders")
          .select("*")
          .eq("id", orderId)
          .single();

        const { data: orderItems } = await supabase
          .from("order_items")
          .select("*")
          .eq("order_id", orderId);

        if (order) {
          const completeOrder = {
            id: order.id,
            store_id: order.store_id,
            table_id: order.table_id,
            tableNumber: order.table_number,
            tableName: order.table_name,
            status: order.status as OrderStatus,
            totalAmount: order.total_amount,
            items: orderItems?.map(item => ({
              id: item.id,
              order_id: item.order_id,
              menu_item_id: item.menu_item_id,
              menu_item_name: item.menu_item_name,
              price: item.price,
              quantity: item.quantity,
              created_at: item.created_at,
              updated_at: item.updated_at,
            })) || [],
            specialInstructions: order.special_instructions,
            createdAt: order.created_at,
            updatedAt: order.updated_at,
            is_payed: order.is_payed,
            payment_method: order.payment_method,
            tip_amount: order.tip_amount,
            tip_percentage: order.tip_percentage,
          };

          await handleOrderNotification(completeOrder, 'status_updated', previousStatus);
          console.log('✅ Order cancellation notification sent successfully');
        }
      } catch (notificationError) {
        console.error('❌ Error sending cancellation notification:', notificationError);
        // Don't fail the cancellation if notification fails
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error in cancelOrder:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error cancelling order"
    };
  }
};
